/**
 * Internal Users API Service
 * Handles admin/internal-users endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    CreateInternalUserRequest,
    InternalUserDetailResponse,
    InternalUserFilters,
    InternalUserListResponse,
    RevokeAccessResponse,
    UpdateInternalUserRequest,
} from '@/types/internal'

/**
 * Get all internal users
 * GET /admin/internal-users
 */
export const getInternalUsers = async (
    filters?: InternalUserFilters
): Promise<InternalUserListResponse> => {
    const response = await axiosInstance.get<InternalUserListResponse>(
        API_ROUTES.INTERNAL_USERS.LIST,
        { params: filters }
    )
    return response.data
}

/**
 * Get a single internal user by ID
 * GET /admin/internal-users/{id}
 */
export const getInternalUserById = async (
    id: string
): Promise<InternalUserDetailResponse> => {
    const response = await axiosInstance.get<InternalUserDetailResponse>(
        API_ROUTES.INTERNAL_USERS.DETAIL(id)
    )
    return response.data
}

/**
 * Create a new internal user
 * POST /admin/internal-users
 */
export const createInternalUser = async (
    data: CreateInternalUserRequest
): Promise<InternalUserDetailResponse> => {
    const response = await axiosInstance.post<InternalUserDetailResponse>(
        API_ROUTES.INTERNAL_USERS.CREATE,
        data
    )
    return response.data
}

/**
 * Update an internal user
 * PATCH /admin/internal-users/{id}
 */
export const updateInternalUser = async (
    id: string,
    data: UpdateInternalUserRequest
): Promise<InternalUserDetailResponse> => {
    const response = await axiosInstance.patch<InternalUserDetailResponse>(
        API_ROUTES.INTERNAL_USERS.UPDATE(id),
        data
    )
    return response.data
}

/**
 * Revoke access for an internal user
 * PATCH /admin/internal-users/{id}/revoke-access
 */
export const revokeInternalUserAccess = async (
    id: string
): Promise<RevokeAccessResponse> => {
    const response = await axiosInstance.patch<RevokeAccessResponse>(
        API_ROUTES.INTERNAL_USERS.REVOKE_ACCESS(id)
    )
    return response.data
}
